import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import {
  Home,
  Store,
  Users,
  Package,
  ShoppingBag,
  DollarSign,
  Settings,
  MessageCircle,
  User,
  MapPin,
  ShoppingCart,
  AlertTriangle,
  FolderTree,
  Tag,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from './DashboardLayout';

// Nav items por prefixo de URL. Cada item pode ter `shortcut: '<letra>'`
// que vira `g <letra>` no useKeyboard global do DashboardLayout.
function buildAdminNav() {
  return [
    { label: 'Dashboard', to: '/admin-reviter', icon: Home, end: true, shortcut: 'h' },
    { label: 'Lojas', to: '/admin-reviter/lojas', icon: Store, shortcut: 'l' },
    { label: 'Usuários', to: '/admin-reviter/usuarios', icon: Users, shortcut: 'u' },
  ];
}

function buildSupplierNav(businessType) {
  const items = [
    { label: 'Visão geral', to: '/fornecedor/painel', icon: Home, end: true, shortcut: 'h' },
    { label: 'Pedidos', to: '/fornecedor/painel/pedidos', icon: ShoppingBag, shortcut: 'o' },
    { label: 'Produtos', to: '/fornecedor/painel/produtos', icon: Package, shortcut: 'p' },
    { label: 'Categorias', to: '/fornecedor/painel/categorias', icon: FolderTree, shortcut: 'k' },
    { label: 'Campanhas', to: '/fornecedor/painel/campanhas', icon: Tag, shortcut: 'g' },
  ];
  if (businessType === 'catalog') {
    items.push({ label: 'Leads', to: '/fornecedor/painel/leads', icon: MessageCircle, shortcut: 'l' });
  }
  if (businessType === 'reseller') {
    items.push({ label: 'Revendedores', to: '/fornecedor/painel/revendedores', icon: Users, shortcut: 'r' });
  }
  items.push(
    { label: 'Usuários', to: '/fornecedor/painel/usuarios', icon: Users, shortcut: 'u' },
    { label: 'Pagamentos', to: '/fornecedor/painel/pagamentos', icon: DollarSign, shortcut: 'a' },
    { label: 'Configurações', to: '/fornecedor/painel/configuracoes', icon: Settings, shortcut: 'c' }
  );
  return items;
}

function buildResellerNav() {
  return [
    { label: 'Visão geral', to: '/revendedor/painel', icon: Home, end: true, shortcut: 'h' },
    { label: 'Catálogo', to: '/revendedor/painel/catalogo', icon: Package, shortcut: 'c' },
    { label: 'Pedidos', to: '/revendedor/painel/pedidos', icon: ShoppingBag, shortcut: 'o' },
    { label: 'Pagamentos', to: '/revendedor/painel/pagamentos', icon: DollarSign, shortcut: 'a' },
    { label: 'Perfil', to: '/revendedor/painel/perfil', icon: User, shortcut: 'u' },
  ];
}

function buildClientNav() {
  return [
    { label: 'Início', to: '/minha-conta', icon: Home, end: true, shortcut: 'h' },
    { label: 'Meus pedidos', to: '/minha-conta/pedidos', icon: ShoppingCart, shortcut: 'o' },
    { label: 'Endereços', to: '/minha-conta/enderecos', icon: MapPin, shortcut: 'a' },
  ];
}

const PanelLayout = () => {
  const location = useLocation();
  const { user, logout, currentStore } = useAuth();
  const path = location.pathname;

  let nav = [];
  if (path.startsWith('/admin-reviter')) nav = buildAdminNav();
  else if (path.startsWith('/fornecedor/painel')) nav = buildSupplierNav(currentStore?.businessType);
  else if (path.startsWith('/revendedor')) nav = buildResellerNav();
  else if (path.startsWith('/minha-conta')) nav = buildClientNav();

  // Banner pro MANAGER quando a loja atual está bloqueada pela plataforma.
  // O whitelabel já mostra tela de "indisponível"; aqui sinalizamos no
  // backoffice pra ele reagir (entrar em contato com suporte).
  const showBlockedBanner = path.startsWith('/fornecedor/painel') && currentStore?.isBlocked;

  return (
    <DashboardLayout user={user} nav={nav} onLogout={logout}>
      {showBlockedBanner && (
        <div className="mb-4 rounded-lg border border-warning/40 bg-warning/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-warning mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-semibold text-app-text">Loja bloqueada pela Reviter</p>
            <p className="text-app-text-secondary">
              Sua loja está temporariamente fora do ar para os clientes finais.
              Você ainda consegue gerenciar pedidos e produtos por aqui.
              {currentStore?.supportPhone
                ? <> Para regularizar, entre em contato pelo {currentStore.supportPhone}.</>
                : <> Para regularizar, entre em contato com o suporte.</>}
            </p>
          </div>
        </div>
      )}
      <Outlet />
    </DashboardLayout>
  );
};

export default PanelLayout;
